<script setup lang="ts">
import { computed, ref } from 'vue';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/solid';
import type { CommandEnumOption } from '@/composables/api/useLocationZonePlayerInspectorApi';

/**
 * The searchable command list shared by {@link LzpCommandPicker}'s desktop
 * popover and mobile full-screen modal. With the filter empty it shows the
 * catalogue in firmware-menu order — position-ordered, `menu_name` groups as
 * labelled sections, separators per `should_prefix_separator`. Typing flattens
 * to every issuable command whose name (or group) matches, so a command buried
 * in a group is one keystroke away.
 *
 * When `optionGateReason` is provided, options it flags stay visible but render
 * disabled with the reason as subtext + tooltip — an ineligible command is
 * still discoverable, just not pickable. Absent prop → rendering unchanged.
 */
const props = defineProps<{
    commands: CommandEnumOption[];
    loading?: boolean;
    selected?: CommandEnumOption | null;
    /** Per-option picker gating for the host's targets (see useIssueCommandForm). Absent → no gating. */
    optionGateReason?: (command: CommandEnumOption) => string | null;
}>();

const emit = defineEmits<{ (e: 'select', command: CommandEnumOption): void }>();

const filter = ref('');

type MenuEntry =
    | { kind: 'command'; separator: boolean; command: CommandEnumOption }
    | { kind: 'group'; separator: boolean; label: string; commands: CommandEnumOption[] };

/** Catalogue in menu order: top-level commands + `menu_name` groups, by position. */
const menu = computed<MenuEntry[]>(() => {
    const childrenByMenu = new Map<string, CommandEnumOption[]>();
    for (const row of props.commands) {
        const group = row.menu_name?.trim();
        if (!group || !row.is_issuable) {
            continue;
        }
        const list = childrenByMenu.get(group) ?? childrenByMenu.set(group, []).get(group)!;
        list.push(row);
    }
    for (const list of childrenByMenu.values()) {
        list.sort((a, b) => a.position - b.position);
    }

    const consumed = new Set<string>();
    const entries: MenuEntry[] = [];
    for (const row of props.commands) {
        if (row.menu_name?.trim()) {
            continue;
        }
        const separator = Boolean(row.should_prefix_separator);
        const children = childrenByMenu.get(row.name);
        if (children && children.length > 0) {
            entries.push({ kind: 'group', separator, label: row.name, commands: children });
            consumed.add(row.name);
        } else if (row.is_issuable) {
            entries.push({ kind: 'command', separator, command: row });
        }
    }
    for (const [label, commands] of childrenByMenu) {
        if (!consumed.has(label) && commands.length > 0) {
            entries.push({ kind: 'group', separator: false, label, commands });
        }
    }
    return entries;
});

/** Every issuable command tagged with its group, for the filtered (flat) view. */
const flat = computed<Array<{ command: CommandEnumOption; group: string | null }>>(() =>
    props.commands
        .filter((command) => command.is_issuable)
        .map((command) => ({ command, group: command.menu_name?.trim() || null }))
);

/** Flat matches when filtering by name or group; null while the filter is empty. */
const filtered = computed<Array<{ command: CommandEnumOption; group: string | null }> | null>(() => {
    const query = filter.value.trim().toLowerCase();
    if (query === '') {
        return null;
    }
    return flat.value.filter(
        ({ command, group }) =>
            command.name.toLowerCase().includes(query) || (group ?? '').toLowerCase().includes(query)
    );
});

/** Short muted version-bound hint (mirrors gvadmin "Max …"). */
function boundLabel(command: CommandEnumOption): string {
    const min = command.version_minimum?.trim();
    const max = command.version_maximum?.trim();
    if (min && max) {
        return `${min}–${max}`;
    }
    if (max) {
        return `Max ${max}`;
    }
    if (min) {
        return `Min ${min}`;
    }
    return '';
}

function isSelected(command: CommandEnumOption): boolean {
    return props.selected != null && props.selected.id === command.id;
}

/**
 * Gate reason per catalogue id, computed once per commands/targets change so
 * the template's repeated lookups don't re-run the per-target gating loop.
 */
const gateReasonById = computed<Map<number, string | null>>(() => {
    const reasons = new Map<number, string | null>();
    if (!props.optionGateReason) {
        return reasons;
    }
    for (const command of props.commands) {
        reasons.set(command.id, props.optionGateReason(command));
    }
    return reasons;
});

function gateReason(command: CommandEnumOption): string | null {
    return gateReasonById.value.get(command.id) ?? null;
}
</script>

<template>
    <div class="flex min-h-0 flex-col">
        <!-- Sticky filter box. -->
        <div class="bg-popover border-border/60 sticky top-0 z-10 border-b p-2">
            <div class="relative">
                <MagnifyingGlassIcon class="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2" aria-hidden="true" />
                <input
                    v-model="filter"
                    type="text"
                    placeholder="Filter commands…"
                    autofocus
                    class="border-border/60 focus:border-primary w-full rounded-md border bg-transparent py-1.5 pr-2 pl-8 text-sm outline-none"
                    data-testid="lzp-command-filter"
                    aria-label="Filter commands"
                />
            </div>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto p-1" data-testid="lzp-command-list">
            <p v-if="loading" class="text-muted-foreground px-2 py-1.5 text-xs">Loading commands…</p>

            <!-- Filtered: a flat list of matches with their group as context. -->
            <template v-else-if="filtered">
                <p v-if="filtered.length === 0" class="text-muted-foreground px-2 py-6 text-center text-xs">
                    No commands match “{{ filter }}”.
                </p>
                <button
                    v-for="{ command, group } in filtered"
                    :key="command.id"
                    type="button"
                    class="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm"
                    :class="[
                        gateReason(command) !== null ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/60',
                        isSelected(command) ? 'bg-muted font-medium' : '',
                    ]"
                    :disabled="gateReason(command) !== null"
                    :aria-disabled="gateReason(command) !== null || undefined"
                    :data-gated="gateReason(command) !== null ? 'true' : undefined"
                    :title="gateReason(command) ?? undefined"
                    @click="emit('select', command)"
                >
                    <span class="min-w-0">
                        <span class="block truncate">{{ command.name }}</span>
                        <span v-if="group" class="text-muted-foreground block truncate text-[11px]">{{ group }}</span>
                        <span v-if="gateReason(command)" class="text-muted-foreground block truncate text-[11px]">
                            {{ gateReason(command) }}
                        </span>
                    </span>
                    <span v-if="boundLabel(command)" class="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                        {{ boundLabel(command) }}
                    </span>
                </button>
            </template>

            <!-- Unfiltered: menu order, groups as labelled sections, separators. -->
            <template v-else>
                <template v-for="(entry, index) in menu" :key="entry.kind === 'command' ? entry.command.id : entry.label">
                    <div v-if="entry.separator && index > 0" class="bg-border/60 my-1 h-px" role="separator" />

                    <template v-if="entry.kind === 'group'">
                        <p class="text-muted-foreground px-2 pt-2 pb-1 text-[11px] font-medium tracking-wide uppercase">
                            {{ entry.label }}
                        </p>
                        <button
                            v-for="command in entry.commands"
                            :key="command.id"
                            type="button"
                            class="flex w-full items-center justify-between gap-3 rounded-sm py-1.5 pr-2 pl-4 text-left text-sm"
                            :class="[
                                gateReason(command) !== null ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/60',
                                isSelected(command) ? 'bg-muted font-medium' : '',
                            ]"
                            :disabled="gateReason(command) !== null"
                            :aria-disabled="gateReason(command) !== null || undefined"
                            :data-gated="gateReason(command) !== null ? 'true' : undefined"
                            :title="gateReason(command) ?? undefined"
                            @click="emit('select', command)"
                        >
                            <span class="min-w-0">
                                <span class="block truncate">{{ command.name }}</span>
                                <span v-if="gateReason(command)" class="text-muted-foreground block truncate text-[11px]">
                                    {{ gateReason(command) }}
                                </span>
                            </span>
                            <span v-if="boundLabel(command)" class="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                                {{ boundLabel(command) }}
                            </span>
                        </button>
                    </template>

                    <button
                        v-else
                        type="button"
                        class="flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm"
                        :class="[
                            gateReason(entry.command) !== null ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/60',
                            isSelected(entry.command) ? 'bg-muted font-medium' : '',
                        ]"
                        :disabled="gateReason(entry.command) !== null"
                        :aria-disabled="gateReason(entry.command) !== null || undefined"
                        :data-gated="gateReason(entry.command) !== null ? 'true' : undefined"
                        :title="gateReason(entry.command) ?? undefined"
                        @click="emit('select', entry.command)"
                    >
                        <span class="min-w-0">
                            <span class="block truncate">{{ entry.command.name }}</span>
                            <span v-if="gateReason(entry.command)" class="text-muted-foreground block truncate text-[11px]">
                                {{ gateReason(entry.command) }}
                            </span>
                        </span>
                        <span v-if="boundLabel(entry.command)" class="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                            {{ boundLabel(entry.command) }}
                        </span>
                    </button>
                </template>
            </template>
        </div>
    </div>
</template>
